import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import {
	EDIT_TEMPLATE_FORM_FIELDS,
	useEditPreFillableFormController,
} from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/EditPreFillableForm/useEditPreFillableFormController.ts';
import { GODMODE_ROOT_ROUTES } from '@dealstack/frontend/admin/pages/Routing/Routing.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Button, Divider, Flex, Text } from '@mantine/core';
import { MultiSelect, Select, Textarea, TextInput } from 'react-hook-form-mantine';
import { PRE_FILLABLE_FORM_TEMPLATE_TYPE_OPTIONS } from '@dealstack/shared/lib/common/business/preFillableForms.ts';
import { FormProvider } from 'react-hook-form';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { DocumentViewer } from '@dealstack/frontend/shared/components/DocumentViewer/DocumentViewer';
import { PdfTemplateView } from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/common/PdfTemplateView/PdfTemplateView.tsx';
import { DeleteFormTemplateConfirmationModal } from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/common/DeleteFormTemplateConfirmationModal/DeleteFormTemplateConfirmationModal';
import { ManageCustomDetailsDefinitionsModal } from '@dealstack/frontend/admin/pages/Manage/common/ManageCustomDetailsDefinitionsModal/ManageCustomDetailsDefinitionsModal';
import { useFormTemplateCustomDetailsDefinitions } from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/EditPreFillableForm/useFormTemplateCustomDetailsDefinitions.ts';
import { mapCustomDetailsDefinitionToSource } from '@dealstack/frontend/admin/pages/Manage/common/EditFieldMappingModal/availableSources.ts';
import { categoryOptions } from '@dealstack/frontend/shared/utils/customDetailCategoryOptions.ts';

const EditPreFillableForm = () => {
	const {
		navigate,
		editPreFillableFormTemplateForm,
		assignedProjectsOptions,
		assignedOrganizationsOptions,
		onSubmit,
		isFormSubmitting,
		fileBlob,
		fileName,
		isLoadingData,
		showDeletionDrawer,
		setShowDeletionDrawer,
		deletePreFillableFormTemplateRequest,
		onDeletionConfirm,
		formTemplateId,
	} = useEditPreFillableFormController();

	const {
		onCustomDetailsDefinitionsSubmit,
		customDetailsDefinitions,
		showManageCustomDetailsDefinitionsModal,
		setShowManageCustomDetailsDefinitionsModal,
	} = useFormTemplateCustomDetailsDefinitions(formTemplateId);

	const customDetailsDefinitionsSources = useMemo(() => {
		return customDetailsDefinitions?.map(mapCustomDetailsDefinitionToSource);
	}, [customDetailsDefinitions]);

	return (
		<div>
			{isLoadingData && <InlineLoader />}
			<Button
				size="sm"
				variant="outline"
				onClick={() => navigate(GODMODE_ROOT_ROUTES.MANAGE_PRE_FILLABLE_FORMS)}
				leftSection={<Icon variant="arrow-left" />}
			>
				Back to table
			</Button>
			<FormProvider {...editPreFillableFormTemplateForm}>
				<Flex direction="column" gap="1rem" mt="1rem">
					<Flex gap="2rem" align="center" justify="space-between" mt="1rem">
						<Text size="B4Bold">Edit pre-fillable form template</Text>

						<Flex gap="1rem">
							<Button
								size="md"
								variant="deletion"
								onClick={() => setShowDeletionDrawer(true)}
								loading={deletePreFillableFormTemplateRequest.isPending}
							>
								Delete form template
							</Button>

							<Button
								size="md"
								variant="primary"
								onClick={editPreFillableFormTemplateForm.handleSubmit(onSubmit)}
								loading={isFormSubmitting}
							>
								Save changes
							</Button>
						</Flex>
					</Flex>

					<Divider />

					<Box>
						<Flex direction="column" gap="1rem">
							<Flex gap="1rem" w="100%">
								<TextInput
									name={EDIT_TEMPLATE_FORM_FIELDS.NAME}
									control={editPreFillableFormTemplateForm.control}
									label="Template name"
									placeholder="Enter template name"
									size="md"
									w="100%"
								/>
								<Select
									name={EDIT_TEMPLATE_FORM_FIELDS.TYPE}
									control={editPreFillableFormTemplateForm.control}
									data={PRE_FILLABLE_FORM_TEMPLATE_TYPE_OPTIONS}
									label="Template type"
									placeholder="Select template type"
									size="md"
									w="100%"
									disabled
								/>
							</Flex>
							<Flex gap="1rem" w="100%">
								<MultiSelect
									name={EDIT_TEMPLATE_FORM_FIELDS.ASSIGNED_PROJECTS}
									control={editPreFillableFormTemplateForm.control}
									data={assignedProjectsOptions}
									label="Assigned deals"
									placeholder="Select deals you want to assign with template"
									size="md"
									w="100%"
									searchable
									clearable
									sx={(theme) => ({
										value: { backgroundColor: theme.colors.blue[4] },
									})}
								/>
								<Textarea
									name={EDIT_TEMPLATE_FORM_FIELDS.INSTRUCTION}
									control={editPreFillableFormTemplateForm.control}
									label="Instruction"
									size="md"
									placeholder="Instruction to the investors"
									w="100%"
								/>
							</Flex>
							<Flex gap="1rem" w="100%">
								<MultiSelect
									name={EDIT_TEMPLATE_FORM_FIELDS.ASSIGNED_ORGANIZATIONS}
									control={editPreFillableFormTemplateForm.control}
									data={assignedOrganizationsOptions}
									label="Assigned organisations"
									placeholder="New programmes of these organisations get the form"
									size="md"
									w="100%"
									searchable
									clearable
									sx={(theme) => ({
										value: { backgroundColor: theme.colors.blue[4] },
									})}
								/>
								<Select
									name={EDIT_TEMPLATE_FORM_FIELDS.CATEGORY}
									control={editPreFillableFormTemplateForm.control}
									data={categoryOptions}
									label="Template category"
									placeholder="Select template category"
									size="md"
									w="100%"
									clearable
								/>
							</Flex>
						</Flex>
					</Box>

					<Box>
						<Flex justify="space-between">
							<Text size="B5Medium" mb="1rem">
								File name: {fileName}
							</Text>

							<Button
								size="sm"
								h="2rem"
								variant="primary"
								onClick={() => {
									setShowManageCustomDetailsDefinitionsModal(true);
								}}
							>
								Manage custom details definitions
							</Button>
						</Flex>

						{!!fileBlob && (
							<PdfTemplateView
								fileBlob={fileBlob}
								fillableFields={editPreFillableFormTemplateForm.watch(
									EDIT_TEMPLATE_FORM_FIELDS.FILLABLE_FIELDS_MAPPING
								)}
								customDetailsDefinitionsSources={customDetailsDefinitionsSources}
							/>
						)}
					</Box>
				</Flex>

				{showManageCustomDetailsDefinitionsModal && (
					<ManageCustomDetailsDefinitionsModal
						opened={showManageCustomDetailsDefinitionsModal}
						onClose={() => setShowManageCustomDetailsDefinitionsModal(false)}
						initialCustomDefinitions={customDetailsDefinitions ?? []}
						onSubmit={onCustomDetailsDefinitionsSubmit}
						hideEntityTypeInput
					/>
				)}
			</FormProvider>
			{showDeletionDrawer && (
				<DeleteFormTemplateConfirmationModal
					onSubmit={onDeletionConfirm}
					onClose={() => setShowDeletionDrawer(false)}
				/>
			)}
		</div>
	);
};

const Box = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem;
	width: 100%;
	border-radius: 0.5rem;
	background-color: ${(props) => props.theme.v3.colors.gray[2]};

	${DocumentViewer} {
		input.highlighted-pdf-input-valid {
			border: 1px solid ${(props) => props.theme.v3.colors.green[8]} !important;
		}
		input.highlighted-pdf-input-invalid {
			border: 1px solid ${(props) => props.theme.v3.colors.red[6]} !important;
		}
	}
`;
const _EditPreFillableFormExport = memo(EditPreFillableForm);

export { _EditPreFillableFormExport as EditPreFillableForm };
